/**
 * @fileoverview Match3 Tile Component
 * @description Individual gem tile with animations and explosion effects
 */

import React, { useEffect, useRef, useState } from "react";
import { Animated, Pressable, StyleSheet, View, Text } from "react-native";
import { Match3Tile as TileType, GEM_COLORS, SPECIAL_TILE_INFO } from "../types";
import { useThemeColors } from "@/providers/ThemeProvider";

interface Match3TileProps {
  tile: TileType;
  size: number;
  onPress: () => void;
  disabled?: boolean;
}

/** Number of particles in explosion */
const PARTICLE_COUNT = 8;

/**
 * Explosion Particle component
 */
const ExplosionParticle: React.FC<{
  index: number;
  color: string;
  size: number;
  trigger: boolean;
}> = ({ index, color, size, trigger }) => {
  const translateX = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(0)).current;
  const scale = useRef(new Animated.Value(0)).current;
  const opacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (trigger) {
      // Calculate direction based on index
      const angle = (index / PARTICLE_COUNT) * Math.PI * 2;
      const distance = size * 0.8;
      const targetX = Math.cos(angle) * distance;
      const targetY = Math.sin(angle) * distance;

      // Reset and animate
      translateX.setValue(0);
      translateY.setValue(0);
      scale.setValue(1);
      opacity.setValue(1);

      Animated.parallel([
        Animated.timing(translateX, {
          toValue: targetX,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.timing(translateY, {
          toValue: targetY,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.sequence([
          Animated.timing(scale, {
            toValue: 1.2,
            duration: 100,
            useNativeDriver: true,
          }),
          Animated.timing(scale, {
            toValue: 0,
            duration: 200,
            useNativeDriver: true,
          }),
        ]),
        Animated.timing(opacity, {
          toValue: 0,
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [trigger, index, size, translateX, translateY, scale, opacity]);

  const particleSize = size * 0.15;

  return (
    <Animated.View
      pointerEvents="none"
      style={[
        styles.particle,
        {
          width: particleSize,
          height: particleSize,
          backgroundColor: color,
          transform: [
            { translateX },
            { translateY },
            { scale },
          ],
          opacity,
        },
      ]}
    />
  );
};

/**
 * Individual gem tile component with press and animation support
 */
export const Match3Tile: React.FC<Match3TileProps> = ({
  tile,
  size,
  onPress,
  disabled = false,
}) => {
  const colors = useThemeColors();
  const scaleAnim = useRef(new Animated.Value(1)).current;
  const opacityAnim = useRef(new Animated.Value(1)).current;
  const rotateAnim = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(tile.isNew ? -size : 0)).current;
  const [showExplosion, setShowExplosion] = useState(false);

  const gemColors = GEM_COLORS[tile.gemType];
  const specialInfo = SPECIAL_TILE_INFO[tile.specialType];

  // Handle selection animation
  useEffect(() => {
    if (tile.isSelected) {
      Animated.loop(
        Animated.sequence([
          Animated.timing(scaleAnim, {
            toValue: 1.15,
            duration: 200,
            useNativeDriver: true,
          }),
          Animated.timing(scaleAnim, {
            toValue: 1,
            duration: 200,
            useNativeDriver: true,
          }),
        ])
      ).start();
    } else {
      scaleAnim.stopAnimation();
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 100,
        useNativeDriver: true,
      }).start();
    }
  }, [tile.isSelected, scaleAnim]);

  // Handle removal animation with explosion
  useEffect(() => {
    if (tile.isRemoving) {
      // Trigger explosion particles
      setShowExplosion(true);
      
      // Animate the gem out with a spin and scale
      Animated.parallel([
        Animated.sequence([
          // Quick pop up
          Animated.timing(scaleAnim, {
            toValue: 1.3,
            duration: 80,
            useNativeDriver: true,
          }),
          // Then shrink away
          Animated.timing(scaleAnim, {
            toValue: 0,
            duration: 150,
            useNativeDriver: true,
          }),
        ]),
        Animated.timing(rotateAnim, {
          toValue: 1,
          duration: 230,
          useNativeDriver: true,
        }),
        Animated.timing(opacityAnim, {
          toValue: 0,
          duration: 230,
          useNativeDriver: true,
        }),
      ]).start(() => {
        // Reset explosion after animation
        setTimeout(() => setShowExplosion(false), 100);
      });
    } else {
      opacityAnim.setValue(1);
      rotateAnim.setValue(0);
    }
  }, [tile.isRemoving, scaleAnim, opacityAnim, rotateAnim]);

  // Handle falling/new tile animation with bounce
  useEffect(() => {
    if (tile.isNew || tile.isFalling) {
      translateY.setValue(-size * 1.2);
      Animated.spring(translateY, {
        toValue: 0,
        tension: 120,
        friction: 7,
        useNativeDriver: true,
      }).start();
    }
  }, [tile.isNew, tile.isFalling, translateY, size]);

  const handlePress = () => {
    if (!disabled && !tile.isRemoving) {
      onPress();
    }
  };

  const innerSize = size - 4; // Padding
  
  // Rotation interpolation for explosion
  const rotateInterpolate = rotateAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "180deg"],
  });

  return (
    <Pressable
      onPress={handlePress}
      disabled={disabled}
      style={[
        styles.container,
        {
          width: size,
          height: size,
        },
      ]}
      accessibilityRole="button"
      accessibilityLabel={`${tile.gemType} gem${tile.specialType !== "none" ? ` with ${specialInfo.label}` : ""}`}
    >
      {/* Explosion particles */}
      {showExplosion && (
        <View style={styles.explosionContainer} pointerEvents="none">
          {Array.from({ length: PARTICLE_COUNT }).map((_, i) => (
            <ExplosionParticle
              key={i}
              index={i}
              color={gemColors.glow}
              size={size}
              trigger={showExplosion}
            />
          ))}
        </View>
      )}

      <Animated.View
        style={[
          styles.tile,
          {
            width: innerSize,
            height: innerSize,
            backgroundColor: gemColors.primary,
            borderColor: tile.isSelected ? colors.warning : gemColors.secondary,
            borderWidth: tile.isSelected ? 3 : 2,
            shadowColor: gemColors.glow,
            transform: [
              { scale: scaleAnim },
              { translateY: translateY },
              { rotate: rotateInterpolate },
            ],
            opacity: opacityAnim,
          },
        ]}
      >
        {/* Gem inner glow */}
        <View
          style={[
            styles.innerGlow,
            {
              backgroundColor: gemColors.glow,
            },
          ]}
        />
        
        {/* Gem shine effect */}
        <View style={styles.shineEffect} />
        
        {/* Special tile indicator */}
        {tile.specialType !== "none" && (
          <View style={styles.specialIndicator}>
            <Text style={styles.specialEmoji}>{specialInfo.icon}</Text>
          </View>
        )}
      </Animated.View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    justifyContent: "center",
    alignItems: "center",
  },
  tile: {
    borderRadius: 10,
    justifyContent: "center",
    alignItems: "center",
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.4,
    shadowRadius: 5,
    elevation: 5,
    overflow: "hidden",
  },
  innerGlow: {
    position: "absolute",
    top: 4,
    left: 4,
    right: 4,
    height: "40%",
    borderRadius: 6,
    opacity: 0.6,
  },
  shineEffect: {
    position: "absolute",
    top: 2,
    left: "20%",
    width: "30%",
    height: 4,
    backgroundColor: "rgba(255, 255, 255, 0.5)",
    borderRadius: 2,
  },
  specialIndicator: {
    position: "absolute",
    bottom: 2,
    right: 2,
  },
  specialEmoji: {
    fontSize: 12,
  },
  explosionContainer: {
    position: "absolute",
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
  },
  particle: {
    position: "absolute",
    borderRadius: 100,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.5,
    shadowRadius: 2,
    elevation: 3,
  },
});

export default Match3Tile;

